"use client";

// CopyBlock — a block of text you are meant to take with you, with the taking
// built in.
//
// Every command and every link we print for a reader exists to be pasted
// somewhere else: `claude mcp add …` in a guide, the paste line on a skill, a
// snippet inside a skill's body. Printing it and leaving the reader to select
// it by hand is asking them to do the one thing a page can do for them, and
// selecting a wrapped command with a mouse is genuinely fiddly. So the block
// carries its own control: one press, and it is on the clipboard.
//
// It is not a box in the sense law_no_boxes forbids. The faint fill is the code
// block the house already had; what is added is a hairline that firms on hover
// and a control that was always in the corner — the block reads as an object
// you can act on, which is what it is.
//
// Used by both markdown renderers (the docs/company one and the skill one), so
// a snippet behaves identically wherever it is read.

import { useCallback, useRef, useState } from "react";

function Mark({ done }: { done: boolean }) {
  return done ? (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7.4 5.8 10.1 11 4.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="13" height="13" viewBox="0 0 17 17" fill="none" aria-hidden>
      <path d="M5.5 5.5h8v9h-8z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M3.5 11V2.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

export default function CopyBlock({
  text,
  lang,
  className,
  children,
  raw = false,
}: {
  /** exactly what lands on the clipboard — the source, never the rendered html. */
  text: string;
  lang?: string;
  className?: string;
  /** the rendered block. Omitted, the text is printed as it stands. */
  children?: React.ReactNode;
  /** the caller brings its own block (its own <pre>, its own styling) and this
   *  only adds the control over it. */
  raw?: boolean;
}) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return; // the text is on screen and selectable — that is the fallback
    }
    setDone(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1800);
  }, [text]);

  return (
    <div className={`copy-block${className ? ` ${className}` : ""}`} data-lang={lang || undefined}>
      {raw ? children : <pre className="sk-pre">{children ?? <code>{text}</code>}</pre>}
      <button
        type="button"
        className="copy-block__take"
        onClick={() => void copy()}
        aria-label={done ? "copied" : "copy to clipboard"}
      >
        <Mark done={done} />
        <span aria-live="polite">{done ? "copied" : "copy"}</span>
      </button>
    </div>
  );
}
